import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { money, fileUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, Upload, Loader2, CheckCircle2 } from "lucide-react";

export default function CustomerOrderDetail() {
  const { slug, oid } = useParams();
  const fileRef = useRef();
  const [o, setO] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/shop/orders/${oid}`);
    setO(data);
  }, [oid]);

  useEffect(() => { load(); }, [load]);
  if (!o) return <FullScreenLoader />;

  const submitProof = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const path = await uploadFile(f);
      await api.post(`/shop/orders/${oid}/proof`, { file_path: path });
      toast.success("Proof of payment submitted");
      load();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const ps = o.payment_status;
  const PAY_LABEL = { pending_approval: "Pending Approval", partially_paid: "Partially Paid", paid: "Paid", rejected: "Rejected", unpaid: "Unpaid", due: "Due" };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <Link to={`/store/${slug}/orders`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to orders</Link>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight">{o.order_number}</h1>
        <StatusBadge status={o.status} />
        <span className="text-sm font-medium" data-testid="cust-payment-status">Payment: {PAY_LABEL[ps] || ps}</span>
      </div>
      {ps === "pending_approval" && <p className="text-sm text-muted-foreground mb-6" data-testid="awaiting-msg">Your order has been received and your payment is awaiting approval by the store.</p>}

      <Card className="rounded-xl border-border overflow-hidden mb-6">
        <div className="divide-y divide-border">
          {o.items.map((it, i) => (
            <div key={i} className="flex items-center justify-between p-4">
              <div><p className="font-medium text-sm">{it.name}</p><p className="text-xs text-muted-foreground">{money(it.price)} × {it.quantity}</p></div>
              <span className="font-semibold text-sm">{money(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border space-y-1.5 bg-secondary/30 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(o.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground capitalize">{o.fulfillment_method}</span><span>{money(o.fee)}</span></div>
          <div className="flex justify-between font-bold text-base pt-1.5 border-t border-border"><span>Total</span><span>{money(o.total)}</span></div>
          {ps === "partially_paid" && <div className="flex justify-between text-amber-600"><span>Remaining balance</span><span>{money(o.remaining_balance)}</span></div>}
        </div>
      </Card>

      <Card className="p-5 rounded-xl border-border mb-6">
        {o.payment_method === "cod" ? (
          <>
            <h3 className="font-semibold mb-2">Cash on Delivery</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{o.payment_instructions || "Payment will be collected when your order is delivered."}</p>
            <div className="mt-4">
              {ps === "paid" ? (
                <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm" data-testid="cod-paid"><CheckCircle2 className="h-4 w-4" /> Payment received</div>
              ) : (
                <div className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-sm font-medium" data-testid="cod-due">Payment due on delivery</div>
              )}
            </div>
          </>
        ) : (
          <>
            <h3 className="font-semibold mb-2 capitalize">Pay with {o.payment_method}</h3>
            {o.payment_instructions && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{o.payment_instructions}</p>}
            <div className="mt-4">
              {ps === "paid" ? (
                <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm"><CheckCircle2 className="h-4 w-4" /> Payment approved</div>
              ) : ps === "rejected" ? (
                <div className="space-y-3">
                  <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3">
                    <p className="text-sm font-medium text-destructive">Payment was rejected.</p>
                    {o.reject_reason && <p className="text-sm text-destructive/80 mt-0.5">Reason: {o.reject_reason}</p>}
                    <p className="text-xs text-muted-foreground mt-1">Please upload a new payment proof below.</p>
                  </div>
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="replace-proof-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload replacement proof
                  </Button>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" hidden onChange={submitProof} />
                </div>
              ) : o.proof_file_path ? (
                <div className="space-y-3">
                  <p className="text-sm text-amber-600 font-medium">{ps === "partially_paid" ? "Partial payment confirmed by the store." : "Proof submitted — awaiting approval."}</p>
                  {!o.proof_file_path.toLowerCase().endsWith(".pdf") && <img src={fileUrl(o.proof_file_path)} alt="Proof" className="max-h-48 rounded-lg border border-border" />}
                </div>
              ) : (
                <>
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="upload-proof-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload proof of payment
                  </Button>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" hidden onChange={submitProof} />
                </>
              )}
            </div>
          </>
        )}
      </Card>

      <Card className="p-5 rounded-xl border-border">
        <h3 className="font-semibold mb-3">Timeline</h3>
        <div className="space-y-3">
          {[...o.timeline].reverse().map((t, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
              <div><p className="text-sm font-medium">{t.status}</p><p className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
